"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mapIndividualRows = mapIndividualRows;
exports.mapTeamCurrentRoundRows = mapTeamCurrentRoundRows;
exports.mapTeamBestRoundRows = mapTeamBestRoundRows;
exports.mapScorecardRows = mapScorecardRows;
const courseDataHelpers_1 = require("../Utils/courseDataHelpers");
const stablefordUtils_1 = require("./stablefordUtils");
// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------
/** Net-adjusted stroke count for a single hole. Returns 0 when unplayed. */
function netScore(rawScore, hcpAdj, isNet) {
    if (!rawScore)
        return 0;
    return rawScore - (isNet ? hcpAdj : 0);
}
/** Abbreviated label for a player entry. Handles pair registrations sharing an _id. */
function getInitials(scoreData, scoreId) {
    const regs = scoreData.filter((s) => s._id === scoreId);
    if (regs.length > 1) {
        return regs.map((s) => s.playerName.split(' ').map((n) => n[0]).join('')).join(' / ');
    }
    if (regs.length === 1) {
        const name = regs[0].playerName.trim();
        return name.length > 4 ? name.substring(0, 4) : name;
    }
    return '';
}
/**
 * Map raw hole scores into display-ready form and compute front/back/total/totalPar.
 *
 * displayScore is always strokes (gross or net) so the main score row renders
 * normally with holeValueStyle (birdie circles, bogey squares, etc.).
 * For Stableford rounds, stablefordPoints is also populated on each hole.
 */
function buildHoleData(holeScores, isNet, stableford, holePoints) {
    let front = 0;
    let back = 0;
    let totalPar = 0;
    let frontPoints = 0;
    let backPoints = 0;
    const mappedHoles = holeScores.map((hole) => {
        const raw = hole.playerScore ?? 0;
        const strokes = netScore(raw, hole.playerHcpAdj ?? 0, isNet);
        const pts = stableford
            ? (0, stablefordUtils_1.calculateHoleStablefordPoints)(raw, hole.par, hole.playerHcpAdj ?? 0, isNet, holePoints)
            : undefined;
        if (raw !== 0 && raw !== null) {
            totalPar += hole.par;
            if (hole.holeNumber <= 9) {
                front += strokes;
                if (stableford && pts !== undefined)
                    frontPoints += pts;
            }
            else {
                back += strokes;
                if (stableford && pts !== undefined)
                    backPoints += pts;
            }
        }
        return {
            holeNumber: hole.holeNumber,
            displayScore: strokes,
            stablefordPoints: pts,
            playerHcpAdj: hole.playerHcpAdj ?? 0,
            par: hole.par,
        };
    });
    return { mappedHoles, front, back, totalPar, frontPoints, backPoints };
}
// ---------------------------------------------------------------------------
// Public mapper functions
// ---------------------------------------------------------------------------
/**
 * Individual player — current round.
 * Used when LeaderboardPairs opens a solo scorecard.
 */
function mapIndividualRows(scoreData, currentRound, isNet, courseData, eventData) {
    const activeRoundData = scoreData.roundScores?.[scoreData.roundNumber - 1];
    const holeScores = activeRoundData?.holeScores ?? [];
    const roundCourseID = eventData?.rounds?.[currentRound - 1]?.courseID;
    const course = (0, courseDataHelpers_1.getCourseById)(courseData, roundCourseID);
    const teeSet = (0, courseDataHelpers_1.getTeeSetFromCourse)(course, scoreData?.playerInfo?.[0]?.teeSet);
    const showNetDots = eventData?.rounds?.[currentRound - 1]?.scoreFormat?.toLowerCase() !== 'gross';
    const playType = eventData?.rounds?.[currentRound - 1]?.playType;
    const stableford = (0, stablefordUtils_1.isStablefordPlayType)(playType);
    const holePoints = eventData?.rounds?.[currentRound - 1]?.holePoints ?? [];
    if (!holeScores.length) {
        return { rows: [], teeSet, totalHoles: 18, showNetDots, isStableford: stableford };
    }
    const { mappedHoles, front, back, totalPar, frontPoints, backPoints } = buildHoleData(holeScores, isNet, stableford, holePoints);
    const row = {
        id: scoreData._id ?? 'solo',
        label: scoreData.playerName ?? '',
        holeScores: mappedHoles,
        totals: {
            front,
            back,
            total: front + back,
            totalPar,
            ...(stableford ? { frontPoints, backPoints, totalPoints: frontPoints + backPoints } : {}),
        },
    };
    return {
        rows: [row],
        teeSet,
        totalHoles: holeScores.length || 18,
        showNetDots,
        isStableford: stableford,
    };
}
/**
 * Team — best ball / worst ball.
 * All members' scores for the current round are shown together.
 */
function mapTeamCurrentRoundRows(scoreData, currentRound, isNet, courseData, eventData) {
    const totalHoles = eventData?.rounds?.[currentRound - 1]?.totalHoles || 18;
    const showNetDots = isNet;
    const roundCourseID = eventData?.rounds?.[currentRound - 1]?.courseID;
    const course = (0, courseDataHelpers_1.getCourseById)(courseData, roundCourseID);
    const teeSet = (0, courseDataHelpers_1.getTeeSetFromCourse)(course, scoreData?.[0]?.playerInfo?.[0]?.teeSet);
    const playType = eventData?.rounds?.[currentRound - 1]?.playType;
    const stableford = (0, stablefordUtils_1.isStablefordPlayType)(playType);
    const holePoints = eventData?.rounds?.[currentRound - 1]?.holePoints ?? [];
    const rows = scoreData.map((score) => {
        const holeScores = score.roundScores?.[currentRound - 1]?.holeScores ?? [];
        const { mappedHoles, front, back, totalPar, frontPoints, backPoints } = buildHoleData(holeScores, isNet, stableford, holePoints);
        return {
            id: score._id,
            label: getInitials(scoreData, score._id),
            holeScores: mappedHoles,
            totals: {
                front,
                back,
                total: front + back,
                totalPar,
                ...(stableford ? { frontPoints, backPoints, totalPoints: frontPoints + backPoints } : {}),
            },
        };
    });
    return { rows, teeSet, totalHoles, showNetDots, isStableford: stableford };
}
/**
 * Team — best round / worst round.
 * Finds each player's best completed round (lowest to-par) across all rounds
 * up to activeRound, then displays that round's scorecard with an "R{n} | Name" label.
 */
function mapTeamBestRoundRows(scoreData, activeRound, isNet, courseData, eventData) {
    const totalHoles = eventData?.rounds?.[activeRound - 1]?.totalHoles || 18;
    const showNetDots = isNet;
    const playType = eventData?.rounds?.[activeRound - 1]?.playType;
    const stableford = (0, stablefordUtils_1.isStablefordPlayType)(playType);
    // Fallback teeSet from the active round
    const fallbackCourseID = eventData?.rounds?.[activeRound - 1]?.courseID;
    const fallbackCourse = (0, courseDataHelpers_1.getCourseById)(courseData, fallbackCourseID);
    const fallbackTeeSet = (0, courseDataHelpers_1.getTeeSetFromCourse)(fallbackCourse, scoreData?.[0]?.playerInfo?.[0]?.teeSet);
    let primaryTeeSet = fallbackTeeSet;
    const rows = scoreData
        .slice()
        .sort((a, b) => a.playerInfo[0].playerName.localeCompare(b.playerInfo[0].playerName))
        .map((score) => {
        // Find the player's best (lowest to-par) completed round
        let bestToPar = Infinity;
        let bestRoundNumber = 1;
        score.roundScores
            .filter((rs) => rs.roundNumber <= activeRound)
            .forEach((rs) => {
            const allScored = rs.holeScores.every((h) => h.playerScore !== 0 && h.playerScore !== null);
            if (!allScored)
                return;
            const rsCourseID = eventData?.rounds?.[rs.roundNumber - 1]?.courseID;
            const rsCourse = (0, courseDataHelpers_1.getCourseById)(courseData, rsCourseID);
            const tees = (0, courseDataHelpers_1.getTeeSetFromCourse)(rsCourse, score.playerInfo[0].teeSet) || fallbackTeeSet;
            let roundToPar = 0;
            rs.holeScores.forEach((h) => {
                if (h.playerScore !== 0 && h.playerScore !== null) {
                    roundToPar += h.playerScore - (tees?.holes?.[h.holeNumber - 1]?.par || 0);
                }
            });
            if (roundToPar < bestToPar) {
                bestToPar = roundToPar;
                bestRoundNumber = rs.roundNumber;
            }
        });
        // Resolve teeSet and hole data for the best round
        const bestCourseID = eventData?.rounds?.[bestRoundNumber - 1]?.courseID;
        const bestCourse = (0, courseDataHelpers_1.getCourseById)(courseData, bestCourseID);
        const bestTeeSet = (0, courseDataHelpers_1.getTeeSetFromCourse)(bestCourse, score.playerInfo[0].teeSet) || fallbackTeeSet;
        if (primaryTeeSet === fallbackTeeSet)
            primaryTeeSet = bestTeeSet;
        const bestRoundHolePoints = eventData?.rounds?.[bestRoundNumber - 1]?.holePoints ?? [];
        const bestRoundPlayType = eventData?.rounds?.[bestRoundNumber - 1]?.playType;
        const bestRoundStableford = (0, stablefordUtils_1.isStablefordPlayType)(bestRoundPlayType);
        const bestRoundHoles = score.roundScores[bestRoundNumber - 1]?.holeScores ?? [];
        const { mappedHoles, front, back, totalPar, frontPoints, backPoints } = buildHoleData(bestRoundHoles, isNet, bestRoundStableford, bestRoundHolePoints);
        return {
            id: score._id,
            label: `R${bestRoundNumber} | ${getInitials(scoreData, score._id)}`,
            holeScores: mappedHoles,
            totals: {
                front,
                back,
                total: front + back,
                totalPar,
                ...(bestRoundStableford ? { frontPoints, backPoints, totalPoints: frontPoints + backPoints } : {}),
            },
        };
    });
    return { rows, teeSet: primaryTeeSet, totalHoles, showNetDots, isStableford: stableford };
}
// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------
const CURRENT_ROUND_FORMATS = ['best ball', 'worst ball', 'high low', 'shamble'];
const BEST_ROUND_FORMATS = ['best round', 'worst round'];
/**
 * Selects and runs the correct mapper based on eventData.playFormat.
 * Returns null for formats that don't use a scorecard grid (e.g. sum scores),
 * so callers can fall back to their own display.
 */
function mapScorecardRows(playFormat, scoreData, activeRound, isNet, courseData, eventData) {
    const fmt = (playFormat || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    if (CURRENT_ROUND_FORMATS.includes(fmt)) {
        return mapTeamCurrentRoundRows(scoreData, activeRound, isNet, courseData, eventData);
    }
    if (BEST_ROUND_FORMATS.includes(fmt)) {
        return mapTeamBestRoundRows(scoreData, activeRound, isNet, courseData, eventData);
    }
    // Unknown / sum-scores format — caller should use its own fallback UI
    return null;
}
